/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

import { db } from './database'
import { Text, TextVersion, PlayAST, VoiceAssignment } from '../models/Play'
import { v4 as uuidv4 } from 'uuid'
import { getHash } from '../../utils/crypto'
import { cleanOrphanAudio } from '../../services/audioCacheService'

// ============================================================================
// Types serveur pour upsert depuis snapshot de sync
// ============================================================================

export interface ServerTextData {
  id: string
  ownerId: string
  title: string
  author?: string
  year?: string
  description?: string
  currentVersionId?: string
  originalVersionId?: string
  deletedAt?: string
  createdAt: string
  updatedAt: string
}

export interface ServerVersionData {
  id: string
  textId: string
  fileName: string
  ast: PlayAST
  contentHash: string
  voiceAssignments: VoiceAssignment[]
  ownerVersionNumber: number
  versionLabel: string
  changeDescription?: string
  parentVersionId?: string
  createdById: string
  deviceId: string
  deletedAt?: string
  createdAt: string
  updatedAt: string
}

// Arguments pour la persistance locale d'une version dont l'ID vient du serveur
export interface CreateVersionFromServerArgs {
  serverData: {
    id: string
    textId: string
    fileName: string
    ast: PlayAST
    contentHash: string
    voiceAssignments: VoiceAssignment[]
    ownerVersionNumber: number
    versionLabel: string
    changeDescription?: string
    parentVersionId?: string
    createdById: string
    deviceId: string
    createdAt: string
    updatedAt: string
  }
}

// Arguments pour la création d'un texte seul (sans version), après succès serveur
export interface CreateTextOnlyArgs {
  userId: string
  serverId: string // ID retourné par le serveur — utilisé comme id local
  title: string
  author?: string
  year?: string
  description?: string
}

// Arguments pour la création d'un texte et de sa première version
interface CreateTextArgs {
  userId: string
  deviceId: string
  fileName: string
  ast: PlayAST
  title: string
  author?: string
  year?: string
  description?: string
  versionLabel?: string
  changeDescription?: string
}

/**
 * Crée un texte seul en IndexedDB, sans version.
 * À utiliser UNIQUEMENT après un succès du POST /texts/ côté serveur.
 * Le syncStatus est SYNCED dès la création car le texte existe déjà sur le serveur.
 *
 * @param args - Données nécessaires à la création (dont serverId = id serveur)
 * @returns L'ID du texte créé (== serverId)
 */
export async function createText(args: CreateTextOnlyArgs): Promise<string> {
  const { userId, serverId, title, author, year, description } = args
  const now = new Date().toISOString()

  const newText: Text = {
    id: serverId,
    ownerId: userId,
    title,
    author,
    year,
    description,
    currentVersionId: undefined as unknown as string,
    originalVersionId: undefined as unknown as string,
    createdAt: now,
    updatedAt: now,
    lastOpenedAt: now,
    syncStatus: 'SYNCED',
    deletedAt: undefined,
    serverId: serverId,
    lastSyncedAt: now,
    serverUpdatedAt: undefined,
  }

  try {
    await db.transaction('rw', db.texts, async () => {
      await db.texts.add(newText)
    })
    return serverId
  } catch (error) {
    console.error('Erreur lors de la création du texte local:', error)
    throw new Error(
      `Impossible de créer le texte localement : ${error instanceof Error ? error.message : 'Erreur inconnue'}`
    )
  }
}

/**
 * Persiste localement une version dont l'ID et les données viennent du serveur.
 * À utiliser UNIQUEMENT après un succès du POST /texts/{textId}/versions côté serveur.
 * Le syncStatus est SYNCED dès la création car la version existe déjà sur le serveur.
 *
 * @param args - Données serveur de la version créée
 * @returns L'ID de la version créée (== serverData.id)
 */
export async function createVersionFromServer(args: CreateVersionFromServerArgs): Promise<string> {
  const { serverData } = args
  const newVersion: TextVersion = {
    id: serverData.id,
    textId: serverData.textId,
    fileName: serverData.fileName,
    ast: serverData.ast,
    contentHash: serverData.contentHash,
    voiceAssignments: serverData.voiceAssignments ?? [],
    ownerVersionNumber: serverData.ownerVersionNumber,
    versionLabel: serverData.versionLabel,
    changeDescription: serverData.changeDescription,
    parentVersionId: serverData.parentVersionId,
    createdById: serverData.createdById,
    deviceId: serverData.deviceId,
    createdAt: serverData.createdAt,
    updatedAt: serverData.updatedAt,
    syncStatus: 'SYNCED',
    serverId: serverData.id,
    deletedAt: undefined,
  }

  try {
    await db.transaction('rw', db.textVersions, async () => {
      await db.textVersions.add(newVersion)
    })
    return serverData.id
  } catch (error) {
    console.error('Erreur lors de la persistance locale de la version:', error)
    throw new Error(
      `Impossible de persister la version localement : ${error instanceof Error ? error.message : 'Erreur inconnue'}`
    )
  }
}

/**
 * Crée un nouveau texte avec sa première version de manière atomique.
 * C'est la méthode à utiliser pour tout import de fichier.
 *
 * @param args - Données nécessaires à la création
 * @returns L'ID du texte créé
 */
export async function createTextWithVersion(args: CreateTextArgs): Promise<string> {
  const {
    userId,
    deviceId,
    fileName,
    ast,
    title,
    author,
    year,
    description,
    versionLabel = 'Version 1',
    changeDescription = `Création à partir de ${fileName}`,
  } = args

  const textId = uuidv4()
  const versionId = uuidv4()
  const now = new Date().toISOString()
  const contentHash = await getHash(JSON.stringify(ast))

  const newText: Text = {
    id: textId,
    ownerId: userId,
    title,
    author,
    year,
    description,
    currentVersionId: versionId,
    originalVersionId: versionId,
    createdAt: now,
    updatedAt: now,
    lastOpenedAt: now,
    syncStatus: 'PENDING',
    deletedAt: undefined,
    serverId: undefined,
    lastSyncedAt: undefined,
    serverUpdatedAt: undefined,
  }

  const newVersion: TextVersion = {
    id: versionId,
    textId: textId,
    fileName: fileName,
    ast: ast,
    contentHash: contentHash,
    voiceAssignments: [], // Initialement vide
    ownerVersionNumber: 1,
    createdById: userId,
    changeDescription: changeDescription,
    createdAt: now,
    syncStatus: 'PENDING',
    deviceId: deviceId,
    versionLabel: versionLabel,
    deletedAt: undefined,
    serverId: undefined,
  }

  try {
    await db.transaction('rw', db.texts, db.textVersions, async () => {
      await db.texts.add(newText)
      await db.textVersions.add(newVersion)
    })
    return textId
  } catch (error) {
    console.error('Erreur lors de la création du texte et de la version:', error)
    throw new Error(
      `Impossible de créer le texte : ${error instanceof Error ? error.message : 'Erreur inconnue'}`
    )
  }
}

/**
 * Récupère tous les textes non supprimés d'un utilisateur,
 * triés par date de dernière ouverture.
 */
export async function getAllTexts(userId: string): Promise<Text[]> {
  try {
    const texts = await db.texts
      .where('ownerId')
      .equals(userId)
      .filter((text) => !text.deletedAt)
      .sortBy('lastOpenedAt')
    return texts.reverse() // Les plus récents en premier
  } catch (error) {
    console.error('Erreur lors de la récupération des textes:', error)
    throw new Error('Impossible de récupérer les textes.')
  }
}

/**
 * Récupère un texte par son ID.
 */
export async function getText(id: string): Promise<Text | undefined> {
  return await db.texts.get(id)
}

/**
 * Récupère une version de texte par son ID.
 */
async function getTextVersion(versionId: string): Promise<TextVersion | undefined> {
  return await db.textVersions.get(versionId)
}

/**
 * Alias for getTextVersion (for compatibility with migration service)
 */
async function getVersionById(versionId: string): Promise<TextVersion | undefined> {
  return await getTextVersion(versionId)
}

/**
 * Récupère toutes les versions d'un texte.
 */
export async function getAllVersionsForText(textId: string): Promise<TextVersion[]> {
  return await db.textVersions.where({ textId: textId }).sortBy('createdAt')
}

/**
 * Met à jour un texte.
 * @param id - ID du texte à mettre à jour
 * @param changes - Modifications à appliquer
 */
export async function updateText(id: string, changes: Partial<Omit<Text, 'id'>>): Promise<number> {
  try {
    return await db.texts.update(id, {
      ...changes,
      updatedAt: new Date().toISOString(),
      syncStatus: 'PENDING',
    })
  } catch (error) {
    console.error(`Erreur lors de la mise à jour du texte ${id}:`, error)
    throw new Error('Impossible de mettre à jour le texte.')
  }
}

/**
 * Met à jour une version de texte.
 * @param version - Version complète avec les modifications
 */
async function updateVersion(version: TextVersion): Promise<void> {
  try {
    await db.textVersions.put(version)
  } catch (error) {
    console.error(`Erreur lors de la mise à jour de la version ${version.id}:`, error)
    throw new Error('Impossible de mettre à jour la version.')
  }
}

/**
 * Marque un texte comme supprimé (soft delete).
 * Ne supprime pas les versions associées pour la synchronisation.
 *
 * @param id - ID du texte à supprimer
 */
export async function softDeleteText(id: string): Promise<number> {
  return updateText(id, {
    deletedAt: new Date().toISOString(),
    syncStatus: 'PENDING',
  })
}

/**
 * Alias pour softDeleteText (pour compatibilité avec l'API)
 */
export async function deleteText(id: string): Promise<number> {
  return softDeleteText(id)
}

/**
 * Supprime physiquement un texte et toutes ses versions d'IndexedDB.
 * À utiliser UNIQUEMENT après confirmation 204 du serveur (ou 404 serveur = déjà supprimé).
 *
 * Étapes atomiques (transaction Dexie) :
 *   1. Récupérer toutes les versions du texte
 *   2. Supprimer les drafts de chaque version
 *   3. Supprimer les compilations des versions
 *   4. Supprimer les versions
 *   5. Supprimer le texte
 * Après transaction : cleanOrphanAudio en best-effort.
 * // TODO prompt 06 : supprimer versionConfigs associées
 *
 * @param textId - ID du texte à supprimer définitivement
 */
export async function deleteTextLocally(textId: string): Promise<void> {
  // Récupérer les IDs des versions avant la transaction pour le nettoyage audio
  const versions = await db.textVersions.where('textId').equals(textId).toArray()
  const versionIds = versions.map((v) => v.id)

  // Collecter les hashes audio AVANT la transaction — les compilations seront
  // effacées dans la transaction et seraient introuvables par cleanOrphanAudio.
  const audioHashesByVersion = await _collectAudioHashes(versionIds)

  await db.transaction(
    'rw',
    [db.texts, db.textVersions, db.versionDrafts, db.compilations, db.versionConfigs],
    async () => {
      // Supprimer les drafts, compilations et configs de chaque version
      for (const versionId of versionIds) {
        await db.versionDrafts.where('versionId').equals(versionId).delete()
        await db.versionConfigs.where('versionId').equals(versionId).delete()
      }

      // Supprimer les compilations liées à ces versions
      if (versionIds.length > 0) {
        await db.compilations.where('textVersionId').anyOf(versionIds).delete()
      }

      // Supprimer les versions
      await db.textVersions.where('textId').equals(textId).delete()

      // Supprimer le texte
      await db.texts.delete(textId)
    }
  )

  // Nettoyage audio best-effort (hors transaction — ne bloque pas si erreur)
  await cleanOrphanAudio(versionIds, audioHashesByVersion)
}

/**
 * Supprime physiquement une version d'IndexedDB et met à jour le texte parent.
 * À utiliser UNIQUEMENT après confirmation 204 ou 404 du serveur.
 *
 * Étapes atomiques (transaction Dexie) :
 *   1. Récupérer la version pour obtenir textId
 *   2. Supprimer le draft associé
 *   3. Supprimer les compilations associées
 *   4. Supprimer les versionConfigs associées
 *   5. Supprimer la version
 *   6. Mettre à jour text.currentVersionId si nécessaire
 * Après transaction : cleanOrphanAudio en best-effort.
 *
 * @param versionId - ID de la version à supprimer définitivement
 */
export async function deleteVersionLocally(versionId: string): Promise<void> {
  // Collecter les hashes audio AVANT la transaction — les compilations seront
  // effacées dans la transaction et seraient introuvables par cleanOrphanAudio.
  const audioHashesByVersion = await _collectAudioHashes([versionId])

  await db.transaction(
    'rw',
    [db.texts, db.textVersions, db.versionDrafts, db.compilations, db.versionConfigs],
    async () => {
      const version = await db.textVersions.get(versionId)
      if (!version) return // déjà absente — rien à faire

      const textId = version.textId

      // Supprimer le draft associé
      await db.versionDrafts.where('versionId').equals(versionId).delete()

      // Supprimer les compilations associées
      await db.compilations.where('textVersionId').equals(versionId).delete()

      // Supprimer les versionConfigs associées
      await db.versionConfigs.where('versionId').equals(versionId).delete()

      // Supprimer la version
      await db.textVersions.delete(versionId)

      // Mettre à jour le texte parent
      const text = await db.texts.get(textId)
      if (!text) return

      if (text.currentVersionId === versionId) {
        // Chercher la version restante la plus récente (hors version supprimée)
        const remaining = await db.textVersions
          .where('textId')
          .equals(textId)
          .filter((v) => !v.deletedAt)
          .sortBy('createdAt')

        const latest = remaining.length > 0 ? remaining[remaining.length - 1] : undefined
        const now = new Date().toISOString()
        await db.texts.update(textId, {
          currentVersionId: latest?.id,
          updatedAt: now,
          syncStatus: 'SYNCED' as const,
        })
      }
    }
  )

  // Nettoyage audio best-effort (hors transaction — ne bloque pas si erreur)
  await cleanOrphanAudio([versionId], audioHashesByVersion)
}

/**
 * Collecte les content_hash audio référencés par les compilations des versions données,
 * avant qu'elles soient effacées par la transaction de suppression.
 *
 * @param versionIds - IDs des versions dont on veut collecter les hashes
 * @returns Map versionId → Set<hash> (peut être vide si pas de compilation/manifest)
 */
async function _collectAudioHashes(versionIds: string[]): Promise<Map<string, Set<string>>> {
  const result = new Map<string, Set<string>>()
  if (versionIds.length === 0) return result

  for (const versionId of versionIds) {
    const hashes = new Set<string>()
    const compilations = await db.compilations.where('textVersionId').equals(versionId).toArray()
    for (const compilation of compilations) {
      if (!compilation.audioManifest) continue
      try {
        const manifest = JSON.parse(compilation.audioManifest) as {
          lines?: Array<{ contentHash?: string }>
        }
        for (const line of manifest?.lines ?? []) {
          const hash = line.contentHash
          if (hash) hashes.add(hash)
        }
      } catch {
        // manifest malformé — ignorer
      }
    }
    result.set(versionId, hashes)
  }

  return result
}

/**
 * Récupère un texte avec sa version courante.
 * @param textId - ID du texte
 * @returns Objet avec text et version (peut être null si pas de version), ou null si texte non trouvé
 */
export async function getTextWithCurrentVersion(
  textId: string
): Promise<{ text: Text; version: TextVersion | null } | null> {
  try {
    const text = await getText(textId)
    if (!text || text.deletedAt) {
      return null
    }

    const version = text.currentVersionId
      ? ((await getTextVersion(text.currentVersionId)) ?? null)
      : null

    if (!version && text.currentVersionId) {
      console.warn(`Version courante ${text.currentVersionId} non trouvée pour texte ${textId}`)
    }

    // Mettre à jour lastOpenedAt
    await updateText(textId, { lastOpenedAt: new Date().toISOString() })

    return { text, version }
  } catch (error) {
    console.error(`Erreur lors de la récupération du texte ${textId}:`, error)
    return null
  }
}

/**
 * Récupère tous les textes avec leurs versions courantes.
 * Un texte sans version (currentVersionId absent ou version introuvable) est retourné
 * avec version: null — les callers doivent gérer ce cas.
 *
 * @param userId - ID de l'utilisateur
 * @returns Liste des textes avec leurs versions (version peut être null)
 */
export async function getAllTextsWithVersions(
  userId: string
): Promise<Array<{ text: Text; version: TextVersion | null }>> {
  try {
    const texts = await getAllTexts(userId)
    const results: Array<{ text: Text; version: TextVersion | null }> = []

    for (const text of texts) {
      // Texte sans version : retourner version: null (texte valide, pas encore de version)
      if (!text.currentVersionId) {
        results.push({ text, version: null })
        continue
      }

      let version = await getTextVersion(text.currentVersionId)

      if (!version) {
        // currentVersionId introuvable : arrivé après un reset DB serveur où la version
        // cible était supprimée (deletedAt défini) et donc exclue du re-push.
        // Fallback : chercher la version non-supprimée la plus récente pour ce texte.
        const fallbackVersions = await getAllVersionsForText(text.id)
        const available = fallbackVersions.filter((v) => !v.deletedAt)
        if (available.length > 0) {
          // Prendre la plus récente
          version = available[available.length - 1]
          // Corriger le pointeur local pour que les prochains accès soient corrects
          await db.texts.update(text.id, {
            currentVersionId: version.id,
            syncStatus: 'PENDING' as const,
          })
        } else {
          // Aucune version disponible : texte valide sans version
          results.push({ text, version: null })
          continue
        }
      }

      // Défense contre un AST malformé : garantir que elements et characters existent
      if (!version.ast) {
        results.push({ text, version: null })
        continue
      }
      if (!Array.isArray(version.ast.elements)) {
        version = { ...version, ast: { ...version.ast, elements: [] } }
      }
      if (!Array.isArray(version.ast.characters)) {
        version = { ...version, ast: { ...version.ast, characters: [] } }
      }

      results.push({ text, version })
    }

    return results
  } catch (error) {
    console.error('Erreur lors de la récupération des textes avec versions:', error)
    throw new Error('Impossible de récupérer les textes avec versions.')
  }
}

/**
 * Change la version courante d'un texte.
 * @param textId - ID du texte
 * @param versionId - ID de la nouvelle version courante
 */
export async function switchToVersion(textId: string, versionId: string): Promise<void> {
  try {
    // Vérifier que la version existe et appartient au texte
    const version = await getTextVersion(versionId)
    if (!version || version.textId !== textId) {
      throw new Error('Version invalide ou non trouvée')
    }

    await updateText(textId, {
      currentVersionId: versionId,
    })
  } catch (error) {
    console.error(`Erreur lors du changement de version pour le texte ${textId}:`, error)
    throw new Error('Impossible de changer de version.')
  }
}

/**
 * Crée une nouvelle version d'un texte existant.
 * @param args - Données pour la nouvelle version
 * @returns L'ID de la nouvelle version créée
 */
export async function createNewVersion(args: {
  textId: string
  userId: string
  deviceId: string
  fileName: string
  ast: PlayAST
  changeDescription?: string
}): Promise<string> {
  const { textId, userId, deviceId, fileName, ast, changeDescription } = args

  try {
    // Récupérer le texte parent
    const text = await getText(textId)
    if (!text) {
      throw new Error('Texte non trouvé')
    }

    // Récupérer toutes les versions pour calculer le numéro de version
    const versions = await getAllVersionsForText(textId)
    const maxVersionNumber = Math.max(...versions.map((v) => v.ownerVersionNumber), 0)

    const versionId = uuidv4()
    const now = new Date().toISOString()
    const contentHash = await getHash(JSON.stringify(ast))

    const newVersion: TextVersion = {
      id: versionId,
      textId: textId,
      fileName: fileName,
      ast: ast,
      contentHash: contentHash,
      voiceAssignments: [], // Hériter des assignments de la version précédente si nécessaire
      ownerVersionNumber: maxVersionNumber + 1,
      createdById: userId,
      changeDescription: changeDescription || `Version ${maxVersionNumber + 1}`,
      createdAt: now,
      syncStatus: 'PENDING',
      deviceId: deviceId,
      versionLabel: `Version ${maxVersionNumber + 1}`,
      deletedAt: undefined,
      serverId: undefined,
    }

    await db.transaction('rw', db.texts, db.textVersions, async () => {
      await db.textVersions.add(newVersion)
      await updateText(textId, {
        currentVersionId: versionId,
      })
    })

    return versionId
  } catch (error) {
    console.error('Erreur lors de la création de la nouvelle version:', error)
    throw new Error(
      `Impossible de créer la nouvelle version : ${error instanceof Error ? error.message : 'Erreur inconnue'}`
    )
  }
}

// ============================================================================
// upsertTextFromServer
// ============================================================================

/**
 * Upsert idempotent d'un texte reçu depuis le snapshot de synchronisation.
 * - Si le texte existe : met à jour tous les champs sauf lastOpenedAt (local).
 * - Si absent : crée avec syncStatus SYNCED et lastOpenedAt = updatedAt.
 *
 * @param serverText - Données du texte provenant du serveur
 */
export async function upsertTextFromServer(serverText: ServerTextData): Promise<void> {
  const existing = await db.texts.get(serverText.id)
  if (existing) {
    await db.texts.update(serverText.id, {
      title: serverText.title,
      author: serverText.author,
      year: serverText.year,
      description: serverText.description,
      currentVersionId: serverText.currentVersionId,
      originalVersionId: serverText.originalVersionId,
      updatedAt: serverText.updatedAt,
      syncStatus: 'SYNCED' as const,
      // Le propriétaire récupère son texte via snapshot.texts → jamais en lecture seule
      isShared: false,
      sharedGroupId: undefined,
      // Ne pas écraser lastOpenedAt — c'est une donnée locale
    })
  } else {
    const newText: Text = {
      id: serverText.id,
      ownerId: serverText.ownerId,
      title: serverText.title,
      author: serverText.author,
      year: serverText.year,
      description: serverText.description,
      currentVersionId: serverText.currentVersionId as string,
      originalVersionId: serverText.originalVersionId as string,
      createdAt: serverText.createdAt,
      updatedAt: serverText.updatedAt,
      lastOpenedAt: serverText.updatedAt,
      syncStatus: 'SYNCED',
      deletedAt: undefined,
      serverId: serverText.id,
      lastSyncedAt: serverText.updatedAt,
      serverUpdatedAt: serverText.updatedAt,
    }
    await db.texts.add(newText)
  }
}

// ============================================================================
// upsertVersionFromServer
// ============================================================================

/**
 * Upsert idempotent d'une version reçue depuis le snapshot de synchronisation.
 * - Si la version existe : met à jour tous les champs (le serveur est autoritaire).
 * - Si absente : crée avec syncStatus SYNCED.
 *
 * @param serverVersion - Données de la version provenant du serveur
 */
export async function upsertVersionFromServer(serverVersion: ServerVersionData): Promise<void> {
  const existing = await db.textVersions.get(serverVersion.id)
  if (existing) {
    await db.textVersions.update(serverVersion.id, {
      textId: serverVersion.textId,
      fileName: serverVersion.fileName,
      ast: serverVersion.ast,
      contentHash: serverVersion.contentHash,
      voiceAssignments: serverVersion.voiceAssignments,
      ownerVersionNumber: serverVersion.ownerVersionNumber,
      versionLabel: serverVersion.versionLabel,
      changeDescription: serverVersion.changeDescription,
      parentVersionId: serverVersion.parentVersionId,
      createdById: serverVersion.createdById,
      deviceId: serverVersion.deviceId,
      updatedAt: serverVersion.updatedAt,
      syncStatus: 'SYNCED' as const,
    })
  } else {
    const newVersion: TextVersion = {
      id: serverVersion.id,
      textId: serverVersion.textId,
      fileName: serverVersion.fileName,
      ast: serverVersion.ast,
      contentHash: serverVersion.contentHash,
      voiceAssignments: serverVersion.voiceAssignments,
      ownerVersionNumber: serverVersion.ownerVersionNumber,
      versionLabel: serverVersion.versionLabel,
      changeDescription: serverVersion.changeDescription,
      parentVersionId: serverVersion.parentVersionId,
      createdById: serverVersion.createdById,
      deviceId: serverVersion.deviceId,
      createdAt: serverVersion.createdAt,
      updatedAt: serverVersion.updatedAt,
      syncStatus: 'SYNCED',
      serverId: serverVersion.id,
      deletedAt: undefined,
    }
    await db.textVersions.add(newVersion)
  }
}

/**
 * Repository object exporting all text-related functions
 */
export const textsRepository = {
  createText,
  createTextWithVersion,
  createVersionFromServer,
  upsertTextFromServer,
  upsertVersionFromServer,
  getAllTexts,
  getText,
  getTextVersion,
  getVersionById,
  getAllTextsWithVersions,
  getTextWithCurrentVersion,
  getAllVersionsForText,
  updateText,
  updateVersion,
  deleteText,
  softDeleteText,
  deleteTextLocally,
  deleteVersionLocally,
  createNewVersion,
  switchToVersion,
}
